import apiService from '../../../services/api-client';
import { CompanyRole } from '../../user/interfaces/role';

// Tipos del chatbot de datos (texto -> SQL de solo lectura). Coinciden 1:1
// con AiChat*Schemas en el backend (schemas/ai_chat_schemas.py), que ya
// exponen camelCase via alias_generator=to_camel -- no hace falta traducir
// nada aca.

export enum LlmProvider {
  ANTHROPIC = 'anthropic',
  OPENAI = 'openai',
  GEMINI = 'gemini',
}

export interface AiChatHistoryItem {
  question: string;
  answer: string;
}

export interface AiChatChartData {
  chartType: 'bar' | 'line';
  categories: string[];
  series: number[];
}

export interface AiChatQueryResponse {
  answer: string;
  columns: string[];
  rows: unknown[][];
  sql: string | null;
  rowCount: number;
  chart: AiChatChartData | null;
}

export interface AiChatConfig {
  aiChatEnabled: boolean;
  // Proveedor de LLM activo para esta empresa -- determina cual de las tres
  // API keys hace falta para que el chatbot funcione.
  llmProvider: LlmProvider;
  hasAnthropicApiKey: boolean;
  hasOpenaiApiKey: boolean;
  hasGeminiApiKey: boolean;
  // Roles de la empresa (valores de CompanyRole, ver
  // core/modules/user/interfaces/role.ts) que pueden usar el chatbot,
  // ademas del gate global aiChatEnabled. Tomador de Inventario nunca
  // aparece aca -- ese rol queda siempre afuera, sin importar esta lista.
  allowedRoles: CompanyRole[];
}

// Cada campo es independiente y opcional: undefined = no tocar lo que ya
// estaba guardado (asi la pantalla puede tener varias acciones de guardado
// separadas -- proveedor, cada key, roles -- sin que una pise a la otra).
// Las tres *ApiKey: null/vacio SI borran esa key puntual (sin afectar las
// de los otros proveedores); el GET nunca devuelve el valor real de
// ninguna key, asi que el front no tiene como "reenviarla". allowedRoles
// undefined = no tocar; una lista (incluso vacia) reemplaza la lista
// completa en el backend, no hace merge. aiChatEnabled undefined = no
// tocarlo (el backend lo rechaza igual si quien llama no es superadmin).
export interface AiChatConfigUpdate {
  llmProvider?: LlmProvider;
  anthropicApiKey?: string | null;
  openaiApiKey?: string | null;
  geminiApiKey?: string | null;
  allowedRoles?: CompanyRole[];
  aiChatEnabled?: boolean;
}

export const aiChatService = {
  async query(companyId: number, question: string, history: AiChatHistoryItem[]) {
    const { data } = await apiService().post<AiChatQueryResponse>(`/ai-chat/${companyId}/query`, {
      question,
      history,
    });
    return data;
  },

  async getConfig(companyId: number) {
    const { data } = await apiService().get<AiChatConfig>(`/ai-chat/${companyId}/config`);
    return data;
  },

  async updateConfig(companyId: number, updates: AiChatConfigUpdate) {
    const { data } = await apiService().put<AiChatConfig>(`/ai-chat/${companyId}/config`, updates);
    return data;
  },
};
